/**
 * API Client for DentoXcellence Backend
 * Replaces localStorage operations with SQLite backend calls
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = this.getStoredToken();
    if (this.token && this.isTokenExpired()) {
      this.setToken(null);
    }
  }

  // Token management — sessionStorage clears when browser tab closes
  getStoredToken() {
    return sessionStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      sessionStorage.setItem('auth_token', token);
      const expiresIn = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      sessionStorage.setItem('token_expires', expiresIn.toISOString());
    } else {
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('token_expires');
    }
  }

  isTokenExpired() {
    const expires = sessionStorage.getItem('token_expires');
    if (!expires) return true;
    return new Date() > new Date(expires);
  }

  // HTTP request wrapper
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available (reload token from storage in case it was updated)
    const currentToken = this.getStoredToken();
    if (currentToken && !this.isTokenExpired()) {
      this.token = currentToken;
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle auth errors
      if (response.status === 401 || response.status === 403) {
        this.setToken(null); // Clear invalid token
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle empty responses
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
      
    } catch (error) {
      console.error(`API Request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // File upload method
  async upload(endpoint, formData) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    };

    // Add auth token if available (reload token from storage in case it was updated)
    const currentToken = this.getStoredToken();
    if (currentToken && !this.isTokenExpired()) {
      this.token = currentToken;
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle auth errors
      if (response.status === 401 || response.status === 403) {
        this.setToken(null);
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle JSON response
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
      
    } catch (error) {
      console.error(`Upload failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Authentication API
  async login(credentials) {
    const response = await this.post('/auth/login', credentials);
    if (response.success && response.token) {
      this.setToken(response.token);
      this.token = this.getStoredToken();
    }
    return response;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      this.setToken(null);
    }
  }

  async verifyToken() {
    const currentToken = this.getStoredToken();
    if (currentToken && !this.isTokenExpired()) {
      this.token = currentToken;
    } else {
      this.token = null;
      return { valid: false };
    }

    try {
      const response = await fetch(`${this.baseURL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
      });

      const data = await response.json().catch(() => ({ valid: false }));

      if (response.ok && data.valid) {
        return data;
      }

      if (response.status === 401 || response.status === 403) {
        this.setToken(null);
      }

      return { valid: false, ...data };
    } catch (error) {
      console.warn('Token verification failed:', error);
      return { valid: false };
    }
  }

  async refreshToken() {
    return this.post('/auth/refresh');
  }

  async updateCredentials(credentials) {
    return this.post('/auth/update-credentials', credentials);
  }

  // Content Management API
  async getAllContent() {
    return this.get('/content');
  }

  // Public content access (no auth required)
  async getPublicContent() {
    const url = `${this.baseURL}/content`;
    try {
      const response = await fetch(`${url}?_=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return { success: false, error: 'Invalid response format' };
      }
    } catch (error) {
      console.error('❌ Failed to fetch public content:', error);
      return { success: false, error: error.message };
    }
  }

  async getPageContent(page) {
    return this.get(`/content/${page}`);
  }

  async getContentAtPath(path) {
    return this.get(`/content/path/${path}`);
  }

  async updateContentAtPath(path, value, type) {
    return this.put(`/content/path/${encodeURIComponent(path)}`, { value, type });
  }

  async saveAllContent(content) {
    return this.post('/content/save', { content });
  }

  async getContentHistory(path, limit = 50, offset = 0) {
    return this.get(`/content/history/${path}?limit=${limit}&offset=${offset}`);
  }

  // Media Management API
  async uploadFiles(files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return this.upload('/media/upload', formData);
  }

  async uploadMedia(formData) {
    return this.upload('/media/upload', formData);
  }

  async getMediaList(options = {}) {
    const { page = 1, limit = 20, type } = options;
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (type) params.append('type', type);
    return this.get(`/media?${params}`);
  }

  async getMediaDetails(uuid) {
    return this.get(`/media/${uuid}`);
  }

  async updateMedia(uuid, data) {
    return this.put(`/media/${uuid}`, data);
  }

  async deleteMedia(uuid) {
    return this.delete(`/media/${uuid}`);
  }

  // Content backup & restore (CMS rolling snapshots in KV)
  async listContentBackups() {
    return this.get('/content/backups');
  }

  async createContentBackup(content = null) {
    return this.post('/content/backup', content ? { content } : {});
  }

  async restoreContentBackup(backupKey) {
    return this.post('/content/restore', { backupKey });
  }

  async deleteContentBackup(backupKey) {
    return this.post('/content/backup/delete', { backupKey });
  }

  async updateBackupSettings(frequency) {
    return this.post('/content/backup/settings', { frequency });
  }

  // R2 media snapshots (images, videos, PDFs in archive/ prefix)
  async listMediaBackups() {
    return this.get('/media/backups');
  }

  async createMediaBackupBatch({ backupKey = null, cursor = null, limit = 5, linkedCmsKey = null } = {}) {
    return this.post('/media/backup', { backupKey, cursor, limit, linkedCmsKey });
  }

  async deleteMediaBackupBatch(backupKey, cursor = null) {
    return this.post('/media/backup/delete', { backupKey, cursor });
  }

  async restoreMediaBackupBatch({
    backupKey,
    cursor = null,
    missingOnly = true,
    overwrite = false,
    confirmOverwrite = null,
    limit = 5,
  } = {}) {
    return this.post('/media/restore', {
      backupKey,
      cursor,
      missingOnly,
      overwrite,
      confirmOverwrite,
      limit,
    });
  }

  // System API
  async getSystemStats() {
    return this.get('/system/stats');
  }

  async createBackup() {
    return this.post('/system/backup');
  }

  async getBackups() {
    return this.get('/system/backups');
  }

  async downloadBackup(filename) {
    // Return the download URL
    return `${this.baseURL}/system/download-backup/${filename}`;
  }

  async deleteBackup(filename) {
    return this.delete(`/system/backups/${filename}`);
  }

  async getSystemLogs(limit = 100) {
    return this.get(`/system/logs?limit=${limit}`);
  }

  async cleanupSystem(days = 30, dryRun = false) {
    return this.post('/system/cleanup', { days, dry_run: dryRun });
  }

  // Health check
  async checkHealth() {
    return this.get('/health');
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
